import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, CalendarHeart, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Botanical, LotusDivider } from "@/components/Botanical";
import { ServiceIcon, SectionHeading, Overline, CTABand } from "@/components/blocks";
import { api } from "@/lib/api";
import { ASSETS, tel, CREDENTIALS, STAGES, WHY_CHOOSE } from "@/data/site";
import { useContent } from "@/context/ContentContext";
import DigitalVisit from "@/components/DigitalVisit";
import Testimonials from "@/components/Testimonials";

export default function Home() {
  const CLINIC = useContent();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const clinicSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: CLINIC.clinicName,
    description: "Obstetrician & Gynaecologist clinic in Noida offering compassionate women's healthcare.",
    address: { "@type": "PostalAddress", streetAddress: "Shop No. 41A, Vibe Mall, Sector-110", addressLocality: "Noida", addressCountry: "IN" },
    telephone: CLINIC.appointmentMobile,
    email: CLINIC.email,
    medicalSpecialty: ["Obstetric", "Gynecologic"],
    founder: { "@type": "Physician", name: CLINIC.doctorName, jobTitle: CLINIC.title },
  };

  return (
    <div>
      <SEO path="/" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(clinicSchema) }} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blush via-cream to-background pt-28 md:pt-36">
        <Botanical className="pointer-events-none absolute -left-6 top-24 hidden h-72 w-40 opacity-20 md:block" />
        <Botanical className="pointer-events-none absolute -right-6 bottom-10 hidden h-72 w-40 rotate-180 opacity-20 lg:block" />
        <div className="container-clinic grid items-center gap-12 pb-20 md:grid-cols-2 md:pb-28">
          <Reveal>
            <Overline>{CLINIC.clinicName}</Overline>
            <h1 className="mt-5 font-serif text-4xl leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              {CLINIC.heroHeadline}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-taupe md:text-lg">
              {CLINIC.heroSupporting}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/appointment"
                data-testid="hero-book-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 font-sans text-sm font-semibold text-cream shadow-soft transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-terracotta-dark active:scale-95"
              >
                <CalendarHeart className="h-4 w-4" /> Book an Appointment
              </Link>
              <a
                href={tel(CLINIC.appointmentMobile)}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-terracotta/40 bg-cream px-8 py-4 font-sans text-sm font-semibold text-terracotta transition-colors duration-300 hover:bg-blush"
              >
                <Phone className="h-4 w-4" /> Call the Clinic
              </a>
            </div>
            <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-gold/30 bg-cream/70 px-5 py-2.5">
              <span className="font-serif text-2xl text-terracotta">25+</span>
              <span className="text-sm font-medium text-taupe">Years of Experience</span>
            </div>
          </Reveal>

          <Reveal delay={150} className="relative">
            <div className="relative mx-auto max-w-md">
              <div className="absolute -inset-4 rounded-tr-[5rem] rounded-bl-[5rem] bg-rose/25" aria-hidden="true" />
              <img
                src={ASSETS.doctorSaree}
                alt={`${CLINIC.doctorName}, ${CLINIC.title}`}
                className="relative w-full rounded-tr-[5rem] rounded-bl-[5rem] object-cover shadow-lift"
                loading="eager"
              />
              <div className="absolute -bottom-6 -left-4 rounded-2xl border border-gold/30 bg-cream/95 p-4 shadow-card backdrop-blur md:-left-8">
                <p className="font-serif text-lg text-charcoal">{CLINIC.doctorName}</p>
                <p className="text-xs font-medium text-terracotta">{CLINIC.title}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CREDENTIAL STRIP */}
      <section className="border-y border-soft-border bg-cream" data-testid="credential-strip">
        <div className="container-clinic grid grid-cols-2 gap-y-8 py-10 md:grid-cols-4 md:py-12">
          {CREDENTIALS.map((c, i) => (
            <Reveal key={c.small} delay={i * 80} className="px-4 text-center md:border-r md:border-soft-border md:last:border-none">
              <p className="font-serif text-2xl text-terracotta md:text-3xl">{c.big}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-taupe">{c.small}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section className="container-clinic py-20 md:py-28">
        <div className="grid items-center gap-14 md:grid-cols-2">
          <Reveal className="relative order-2 md:order-1">
            <div className="absolute -inset-4 rotate-2 rounded-tl-[4rem] rounded-br-[4rem] bg-blush" aria-hidden="true" />
            <img src={ASSETS.doctorCoat} alt={`${CLINIC.doctorName} at the clinic`} className="relative w-full rounded-tl-[4rem] rounded-br-[4rem] object-cover shadow-card" loading="lazy" />
          </Reveal>
          <Reveal delay={120} className="order-1 md:order-2">
            <Overline>Meet Dr. Mithee Bhanot</Overline>
            <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-charcoal md:text-4xl lg:text-5xl">
              Experienced hands, a compassionate heart
            </h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {CLINIC.qualificationList.map((q) => (
                <span key={q} className="rounded-full border border-gold/30 bg-cream px-3 py-1 text-xs font-medium text-terracotta">{q}</span>
              ))}
            </div>
            <p className="mt-3 font-sans text-sm font-medium text-taupe">{CLINIC.title}</p>
            <p className="mt-5 text-base leading-relaxed text-taupe">
              With <span className="font-medium text-charcoal">25+ years of experience</span> in women's healthcare, Dr. Mithee combines clinical excellence with a deeply personal approach — taking the time to listen, explain and reassure. Her practice is built on trust, individualised care and patient education, so every woman feels informed, respected and supported through each stage of life.
            </p>
            <Link to="/about" data-testid="home-about-btn" className="mt-8 inline-flex items-center gap-2 font-sans text-sm font-semibold text-terracotta link-underline">
              Meet Dr. Mithee <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-cream py-20 md:py-28">
        <div className="container-clinic">
          <Reveal><SectionHeading center overline="Our Care" title="Comprehensive women's healthcare" subtitle="Care categories thoughtfully organised around the needs of women at every stage." /></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 100}>
                <div className="group flex h-full flex-col rounded-3xl border border-soft-border bg-background p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-card">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blush text-terracotta transition-colors group-hover:bg-terracotta group-hover:text-cream">
                    <ServiceIcon name={s.icon} />
                  </span>
                  <h3 className="mt-6 font-serif text-xl text-charcoal">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-taupe">{s.description}</p>
                  <div className="mt-6 flex items-center gap-4">
                    <Link to="/services" className="text-sm font-semibold text-terracotta link-underline">Learn More</Link>
                    <Link to="/appointment" className="text-sm font-medium text-taupe hover:text-terracotta">Book →</Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CARE THROUGH EVERY STAGE (timeline) */}
      <section className="container-clinic py-20 md:py-28">
        <Reveal><SectionHeading center overline="A Lifelong Partnership" title="Care Through Every Stage of Womanhood" subtitle="From the first questions of adolescence to the wisdom of later years — supportive, informed care at every step." /></Reveal>
        <LotusDivider className="mt-8" />
        <div className="relative mt-14">
          <div className="absolute left-4 top-0 h-full w-px bg-gradient-to-b from-gold/40 via-rose/40 to-transparent md:left-1/2" aria-hidden="true" />
          <div className="space-y-8 md:space-y-0">
            {STAGES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 80}>
                <div className={`relative flex md:items-center ${i % 2 === 0 ? "md:justify-start" : "md:justify-end"}`}>
                  <div className={`ml-12 md:ml-0 md:w-[46%] ${i % 2 === 0 ? "md:pr-10 md:text-right" : "md:pl-10"}`}>
                    <div className="rounded-2xl border border-soft-border bg-cream p-6 shadow-soft">
                      <span className="font-serif text-sm text-gold">0{i + 1}</span>
                      <h3 className="mt-1 font-serif text-xl text-charcoal">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-taupe">{s.desc}</p>
                    </div>
                  </div>
                  <span className="absolute left-4 top-6 h-3 w-3 -translate-x-1/2 rounded-full bg-terracotta ring-4 ring-blush md:left-1/2 md:top-1/2 md:-translate-y-1/2" aria-hidden="true" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-charcoal py-20 md:py-28">
        <div className="container-clinic">
          <Reveal><SectionHeading center light overline="Why Women Choose Dr. Mithee" title="Care you can trust" /></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 90}>
                <div className="h-full rounded-3xl border border-cream/10 bg-cream/[0.04] p-8 transition-colors hover:bg-cream/[0.07]">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-terracotta/20 text-rose">
                    <ServiceIcon name={w.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-cream">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-cream/60">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIGITAL VISIT */}
      <DigitalVisit compact />

      {/* PATIENT EXPERIENCE */}
      <section className="container-clinic py-20 md:py-28">
        <div className="grid items-stretch gap-10 md:grid-cols-2">
          <Reveal className="relative overflow-hidden rounded-3xl">
            <img src="/brand/clinic-3.jpg" alt="A calm, welcoming clinic space" className="h-full w-full object-cover" loading="lazy" />
          </Reveal>
          <Reveal delay={120} className="flex flex-col justify-center rounded-3xl bg-blush p-8 md:p-12">
            <Overline>Patient Experience</Overline>
            <h2 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-4xl">A Clinic Where You Feel Heard</h2>
            <p className="mt-5 text-base leading-relaxed text-taupe">
              Every consultation begins with listening. In a private, respectful and comfortable setting, Dr. Mithee takes the time to understand your concerns, explain things clearly and build a long-term relationship of trust — so you always feel cared for, never rushed.
            </p>
            <Link to="/appointment" data-testid="experience-book-btn" className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-sans text-sm font-semibold text-cream transition-colors hover:bg-terracotta-dark">
              Book Your Consultation <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      <CTABand />
    </div>
  );
}
